import { apiService } from './api';
import { PaginationParams } from '../types/common';
import {
  Client,
  City,
  Country,
  CreateClientRequest,
  UpdateClientRequest,
  ClientsResponse,
  CitiesResponse,
  CountriesResponse,
  ClientStatus,
} from '../types/client';

class ClientService {
  // Clients
  async getClients(params?: PaginationParams & { status?: ClientStatus; cityId?: string; name?: string }): Promise<ClientsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.cityId) queryParams.append('cityId', params.cityId);
    if (params?.name) queryParams.append('name', params.name);

    return apiService.get<ClientsResponse>(`/clients?${queryParams.toString()}`);
  }

  async getClient(id: string): Promise<{ client: Client }> {
    return apiService.get<{ client: Client }>(`/clients/${id}`);
  }

  async createClient(data: CreateClientRequest): Promise<{ message: string; client: Client }> {
    return apiService.post<{ message: string; client: Client }>('/clients', data);
  }

  async updateClient(id: string, data: UpdateClientRequest): Promise<{ message: string; client: Client }> {
    return apiService.put<{ message: string; client: Client }>(`/clients/${id}`, data);
  }

  async deleteClient(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/clients/${id}`);
  }

  // Cities
  async getCities(params?: PaginationParams & { countryId?: string }): Promise<CitiesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.countryId) queryParams.append('countryId', params.countryId);

    return apiService.get<CitiesResponse>(`/cities?${queryParams.toString()}`);
  }

  async getCity(id: string): Promise<{ city: City }> {
    return apiService.get<{ city: City }>(`/cities/${id}`);
  }

  // Countries
  async getCountries(params?: PaginationParams): Promise<CountriesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return apiService.get<CountriesResponse>(`/countries?${queryParams.toString()}`);
  }

  async getCountry(id: string): Promise<{ country: Country }> {
    return apiService.get<{ country: Country }>(`/countries/${id}`);
  }

  // Client status operations
  async activateClient(id: string): Promise<{ message: string; client: Client }> {
    return this.updateClient(id, { status: 'ACTIVE' });
  }

  async deactivateClient(id: string): Promise<{ message: string; client: Client }> {
    return this.updateClient(id, { status: 'INACTIVE' });
  }

  async suspendClient(id: string): Promise<{ message: string; client: Client }> {
    return this.updateClient(id, { status: 'SUSPENDED' });
  }


	  // DSS-style actions
	  async confirm(id: string): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/confirm`);
	  }
	  async freeze(id: string): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/freeze`);
	  }
	  async unfreeze(id: string): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/unfreeze`);
	  }
	  async stopDelivery(id: string): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/stop-delivery`);
	  }
	  async resumeDelivery(id: string): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/resume-delivery`);
	  }
	  async addDelivery(id: string, routeDeliveryId?: string): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/add-delivery`, routeDeliveryId ? { routeDeliveryId } : undefined);
	  }
	  async rearrangeMealSize(id: string, payload: { dietId?: string; priceId?: string }): Promise<{ message: string; client: Client }> {
	    return apiService.post<{ message: string; client: Client }>(`/clients/${id}/rearrange-meal-size`, payload);
	  }

  // Bulk operations
  async bulkUpdateClientStatus(clientIds: string[], status: ClientStatus): Promise<{ message: string; count: number }> {
    return apiService.post<{ message: string; count: number }>('/clients/bulk-status', {
      clientIds,
      status,
    });
  }

  // Export functionality
  async exportClients(params?: { status?: ClientStatus; cityId?: string; search?: string; ids?: string[] }): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (params?.status) queryParams.append('status', params.status);
    if (params?.cityId) queryParams.append('cityId', params.cityId);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));


    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/clients/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // Statistics
  async getClientStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    suspended: number;
    newThisMonth: number;
  }> {
    return apiService.get<{
      total: number;
      active: number;
      inactive: number;
      suspended: number;
      newThisMonth: number;
    }>('/clients/stats');
  }

  // Lookup options
  async getLookupOptions(category: string): Promise<{ lookupTables: Array<{ id: string; key: string; value: string; category: string }> }> {
    return apiService.get(`/lookup-tables?category=${category}&limit=1000&status=ACTIVE`);
  }

  // Get all ingredients for Allergies History and Food Dislike
  async getIngredients(params?: PaginationParams): Promise<{ ingredients: Array<{ id: string; name: string }> }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    return apiService.get(`/ingredients?${queryParams.toString()}`);
  }
}

export const clientService = new ClientService();
